// تمدید قرارداد
// زیرفرم — نمایش/ثبت/ویرایش/حذف تمدیدهای یک قرارداد مشخص
import React, { useState, useEffect, useCallback } from 'react';
import { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtAmt = n => (n ? Number(n).toLocaleString('fa-IR') : '');

const FORM = 'FRMCONTRACTTRANSACTION';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL'];

const COLS = [
  { key: 'transaction_no',         label: 'شماره' },
  { key: 'transaction_date',       label: 'تاریخ' },
  { key: 'new_contract_cost_fmt',  label: 'مبلغ جدید',     num: true },
  { key: 'new_contract_end_date',  label: 'سررسید جدید' },
  { key: 'new_outfit_cost_fmt',    label: 'تجهیز کارگاه',  num: true },
  { key: 'remark',                 label: 'توضیح',         wrap: true },
];

const EMPTY = {
  transaction_no: '', transaction_date: '',
  new_contract_cost: '', new_contract_end_date: '',
  new_outfit_cost: '', remark: '',
};

/* ── سلول گرید ── */
function GridCell({ col, val }) {
  const s = { padding: '8px 10px', textAlign: col.num ? 'left' : 'right', whiteSpace: col.wrap ? 'normal' : 'nowrap', fontSize: '12px' };
  return <td style={s}>{col.num ? toFa(val) : val}</td>;
}

/* ── گرید ساده ── */
function Grid({ columns, rows, selectedId, onSelect }) {
  return (
    <div style={{ overflowX: 'auto', maxHeight: '360px', overflowY: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', tableLayout: 'auto' }}>
        <thead>
          <tr style={{ background: '#4a148c', color: '#fff', position: 'sticky', top: 0, zIndex: 2 }}>
            {columns.map(c => <th key={c.key} style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', whiteSpace: 'nowrap' }}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} onClick={() => onSelect(r)}
              style={{ cursor: 'pointer', background: selectedId === r.id ? '#e8eaf6' : i % 2 ? '#fff8e1' : '#fff' }}>
              {columns.map(c => <GridCell key={c.key} col={c} val={r[c.key] ?? ''} />)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '24px', color: '#999' }}>رکوردی یافت نشد</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ── فیلد ساده ── */
function Field({ label, value, onChange, type = 'text', flex = 1, readOnly }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value} onChange={e => onChange && onChange(e.target.value)} type={type}
        readOnly={readOnly} style={{ ...ui.input, background: readOnly ? '#f2f2f2' : '#fff' }} />
    </div>
  );
}


export default function ContractTransActionList({ contractId, contractSubject, onClose }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(null);   // 'new' | 'edit' | null
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const j = await API(`/contracts/${contractId}/transactions/`);
    setLoading(false);
    if (j.ok) {
      const d = (j.data || []).map(r => ({
        ...r,
        new_contract_cost_fmt: fmtAmt(r.new_contract_cost),
        new_outfit_cost_fmt:   fmtAmt(r.new_outfit_cost),
      }));
      setRows(d);
      setSel(prev => (prev && d.find(x => x.id === prev.id)) || d[0] || null);
    }
  }, [contractId]);

  useEffect(() => { if (fb.loaded && fb.open) load(); }, [fb.loaded, fb.open, load]);

  const openNew = () => { setForm(EMPTY); setErr(''); setModal('new'); };
  const openEdit = () => {
    if (!sel) return;
    setForm({
      transaction_no: sel.transaction_no,
      transaction_date: sel.transaction_date,
      new_contract_cost: sel.new_contract_cost || '',
      new_contract_end_date: sel.new_contract_end_date,
      new_outfit_cost: sel.new_outfit_cost || '',
      remark: sel.remark,
    });
    setErr('');
    setModal('edit');
  };

  const save = async () => {
    const body = JSON.stringify(form);
    const j = modal === 'new'
      ? await API(`/contracts/${contractId}/transactions/create/`, { method: 'POST', body })
      : await API(`/contracts/${contractId}/transactions/${sel.id}/update/`, { method: 'PUT', body });
    if (j.ok) { setModal(null); load(); }
    else setErr(j.msg || 'خطا');
  };

  const doDelete = async () => {
    setAsk(null);
    const j = await API(`/contracts/${contractId}/transactions/${sel.id}/delete/`, { method: 'DELETE' });
    if (j.ok) load(); else setMsg(j.msg);
  };
  const askDelete = async () => {
    if (!sel) return;
    const j = await API(`/contracts/${contractId}/transactions/${sel.id}/delete/?check=1`, { method: 'DELETE' });
    if (!j.ok) { setMsg(j.msg); return; }
    setAsk({ lines: ['آیا از حذف این رکورد مطمئن هستید؟'], onYes: doDelete });
  };

  const has = !!sel;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        width: '900px', maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* هدر */}
        <div style={{
          background: 'linear-gradient(135deg,#4a148c,#6a1b9a)', color: '#fff',
          padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>تمدید قرارداد</div>
            {contractSubject && <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>{contractSubject}</div>}
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '14px',
          }}>✕ بستن</button>
        </div>

        {/* نوار ابزار */}
        <div style={{ padding: '10px 20px', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={{ ...ui.btnNew, cursor: 'pointer' }}>＋ جدید</button>}
          {fb.can('BTNEDIT') && <button onClick={openEdit} disabled={!has} style={{ ...ui.btnEdit, opacity: has ? 1 : 0.4, cursor: 'pointer' }}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && <button onClick={askDelete} disabled={!has} style={{ ...ui.btnDel, opacity: has ? 1 : 0.4, cursor: 'pointer' }}>✕ حذف</button>}
          <div style={{ flex: 1 }} />
          <ExcelButton title="تمدید قرارداد" columns={COLS} rows={rows} />
        </div>

        {/* گرید */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 16px' }}>
          {loading && <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>در حال بارگذاری...</div>}
          {!loading && <Grid columns={COLS} rows={rows} selectedId={sel?.id} onSelect={setSel} />}
        </div>
      </div>

      {/* مودال ثبت/ویرایش */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 950, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', width: '680px', maxWidth: '94vw', padding: '0', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg,#4a148c,#6a1b9a)', color: '#fff', padding: '14px 20px', fontSize: '15px', fontWeight: 600 }}>
              {modal === 'new' ? 'ثبت تمدید جدید' : 'ویرایش تمدید'}
            </div>
            <div style={{ padding: '20px' }}>
              {err && <div style={ui.errBox}>⚠️ {err}</div>}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <Field label="شماره" value={form.transaction_no} onChange={v => setForm(f => ({ ...f, transaction_no: v }))} />
                <Field label="تاریخ" value={form.transaction_date} onChange={v => setForm(f => ({ ...f, transaction_date: v }))} />
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <Field label="مبلغ جدید (ریال)" value={form.new_contract_cost} onChange={v => setForm(f => ({ ...f, new_contract_cost: v }))} />
                <Field label="سررسید جدید" value={form.new_contract_end_date} onChange={v => setForm(f => ({ ...f, new_contract_end_date: v }))} />
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <Field label="تجهیز کارگاه (ریال)" value={form.new_outfit_cost} onChange={v => setForm(f => ({ ...f, new_outfit_cost: v }))} />
                <Field label="توضیح" value={form.remark} onChange={v => setForm(f => ({ ...f, remark: v }))} flex={2} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #e0e0e0' }}>
              {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
              {fb.can('BTNOK') && <button onClick={save} style={ui.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* تأیید حذف */}
      {ask && <WarningDialog title="هشدار" lines={ask.lines} confirmLabel="بلی" cancelLabel="خیر" tone="warn"
        onConfirm={ask.onYes} onCancel={() => setAsk(null)} />}

      {/* پیام */}
      {msg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 970, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '400px', textAlign: 'center' }}>
            <div style={{ marginBottom: '16px', fontSize: '14px', lineHeight: 1.8 }}>{msg}</div>
            <button onClick={() => setMsg(null)} style={ui.btnSave}>تأیید</button>
          </div>
        </div>
      )}
    </div>
  );
}
